//TODO: delete this file after new pricing migration
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { CURRENCY_SYMBOLS } from '@/lib/billing/billing';
// Libs
import { FACEBOOK, INSTAGRAM, LINKEDIN, TIKTOK_BUSINESS, TWITTER } from '@/lib/socialProfile';

export const NO_ACTIVE_PLAN = null;
export const TRIAL = 'trial';
export const PLANS = {
    PRO: 'pro',
    ADVANCED: 'advanced',
    ENTERPRISE: 'enterprise'
};
// Needed to keep the correct order on display
export const PLANS_ORDER = [PLANS.PRO, PLANS.ADVANCED, PLANS.ENTERPRISE];

const getFeaturePrice = (plan, feature, t) => {
    if (!plan?.options?.[feature]) {
        return false;
    }

    const symbol = CURRENCY_SYMBOLS[plan?.currency];

    const value = plan.options?.[feature]?.['price_monthly'] ?? 0;

    return t('subscription_invoices_page.price_per_month', {
        price:
            plan?.currency === 'EUR' || plan?.currency === 'BRL' || plan?.currency === 'INR'
                ? value + symbol
                : symbol + value
    });
};

export const PLANS_SLOTS = {
    social_profiles: {
        type: 'price',
        accessor: 'profiles'
    },
    team_members: {
        type: 'price',
        accessor: 'users'
    },
    competitors: {
        type: 'text_with_value',
        accessor: 'competitors'
    },
    hashtags: {
        type: 'text_with_value',
        accessor: 'hashtags'
    },
    customized_feeds: {
        type: 'text_with_value',
        accessor: 'feeds'
    }
};

export const usePlansFeaturesTable = (plans) => {
    const { t } = useTranslation('settings');

    // Get plans details from useBillingPrices
    if (!plans) {
        return {};
    }

    return {
        basic: {
            title: null,
            features: [
                {
                    feature: 'social_profiles',
                    title: t('subscription_invoices_page.social_profiles'),
                    type: 'basic',
                    networkTypes: null,
                    [PLANS.PRO]: plans[PLANS.PRO].options.profiles?.count_included ?? null,
                    [PLANS.ADVANCED]:
                        plans[PLANS.ADVANCED].options.profiles?.count_included ?? null,
                    [PLANS.ENTERPRISE]:
                        plans[PLANS.ENTERPRISE].options.profiles?.count_included ?? null
                },
                {
                    feature: 'additional_social_profiles',
                    title: t('subscription_invoices_page.additional_social_profiles'),
                    type: 'basic',
                    networkTypes: null,
                    [PLANS.PRO]: getFeaturePrice(plans[PLANS.PRO], 'profiles', t),
                    [PLANS.ADVANCED]: getFeaturePrice(plans[PLANS.ADVANCED], 'profiles', t),
                    [PLANS.ENTERPRISE]: getFeaturePrice(plans[PLANS.ENTERPRISE], 'profiles', t)
                },
                {
                    feature: 'team_members',
                    title: t('subscription_invoices_page.team_members'),
                    type: 'basic',
                    networkTypes: null,
                    [PLANS.PRO]: plans[PLANS.PRO].options.users?.count_included ?? null,
                    [PLANS.ADVANCED]: plans[PLANS.ADVANCED].options.users?.count_included ?? null,
                    [PLANS.ENTERPRISE]:
                        plans[PLANS.ENTERPRISE].options.users?.count_included ?? null
                },
                {
                    feature: 'additional_team_members',
                    title: t('subscription_invoices_page.additional_team_members'),
                    type: 'basic',
                    networkTypes: null,
                    [PLANS.PRO]: getFeaturePrice(plans[PLANS.PRO], 'users', t),
                    [PLANS.ADVANCED]: getFeaturePrice(plans[PLANS.ADVANCED], 'users', t),
                    [PLANS.ENTERPRISE]: getFeaturePrice(plans[PLANS.ENTERPRISE], 'users', t)
                }
            ]
        },
        analytics: {
            title: t('subscription_invoices_page.analytics'),
            features: [
                {
                    feature: 'account_analytics',
                    title: t('subscription_invoices_page.account_analytics'),
                    tooltip: t('subscription_invoices_page.account_analytics_tooltip'),
                    type: 'analytics',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true,
                    [PLANS.PRO]: true
                },
                {
                    feature: 'reels_analytics',
                    title: t('subscription_invoices_page.reels_analytics'),
                    tooltip: t('subscription_invoices_page.reels_analytics_tooltip'),
                    type: 'analytics',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.ENTERPRISE]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true
                },
                {
                    feature: 'promoted_posts_analytics',
                    title: t('subscription_invoices_page.promoted_posts_analytics'),
                    tooltip: t('subscription_invoices_page.promoted_posts_analytics_tooltip'),
                    type: 'analytics',
                    networkTypes: [INSTAGRAM],
                    [PLANS.ADVANCED]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'tags_and_mentions_analytics',
                    title: t('subscription_invoices_page.tags_and_mentions_analytics'),
                    tooltip: t('subscription_invoices_page.tags_and_mentions_analytics_tooltip'),
                    type: 'analytics',
                    networkTypes: [INSTAGRAM],
                    [PLANS.ADVANCED]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'custom_dashboards',
                    title: t('subscription_invoices_page.custom_dashboards'),
                    tooltip: t('subscription_invoices_page.custom_dashboards_tooltip'),
                    type: 'analytics',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ADVANCED]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true
                }
            ]
        },
        reporting: {
            title: t('subscription_invoices_page.reporting'),
            features: [
                {
                    feature: 'account_analytics_reports',
                    title: t('subscription_invoices_page.account_analytics_reports'),
                    tooltip: t('subscription_invoices_page.account_analytics_reports_tooltip'),
                    type: 'reporting',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.PRO]: 'XLS / PDF',
                    [PLANS.ADVANCED]: 'XLS / PDF',
                    [PLANS.ENTERPRISE]: 'XLS / PDF'
                },
                {
                    feature: 'competitor_reports',
                    title: t('subscription_invoices_page.competitor_reports'),
                    tooltip: t('subscription_invoices_page.competitor_reports_tooltip'),
                    type: 'reporting',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: 'XLS / PDF',
                    [PLANS.ADVANCED]: 'XLS / PDF',
                    [PLANS.ENTERPRISE]: 'XLS / PDF'
                },
                {
                    feature: 'hashtag_reports',
                    title: t('subscription_invoices_page.hashtag_reports'),
                    tooltip: t('subscription_invoices_page.hashtag_reports_tooltip'),
                    type: 'reporting',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: 'XLS / PDF',
                    [PLANS.ADVANCED]: 'XLS / PDF',
                    [PLANS.ENTERPRISE]: 'XLS / PDF'
                },
                {
                    feature: 'dashboard_reports',
                    title: t('subscription_invoices_page.dashboard_reports'),
                    tooltip: t('subscription_invoices_page.dashboard_reports_tooltip'),
                    type: 'reporting',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.PRO]: 'XLS / PDF',
                    [PLANS.ADVANCED]: 'XLS / PDF',
                    [PLANS.ENTERPRISE]: 'XLS / PDF'
                },
                {
                    feature: 'dashboard_report_customization',
                    title: t('subscription_invoices_page.dashboard_report_customization'),
                    tooltip: t('subscription_invoices_page.dashboard_report_customization_tooltip'),
                    type: 'reporting',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.PRO]: false,
                    [PLANS.ADVANCED]: false,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'company_branded_pdf_reports',
                    title: t('subscription_invoices_page.company_branded_pdf_reports'),
                    tooltip: t('subscription_invoices_page.company_branded_pdf_reports_tooltip'),
                    type: 'reporting',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.PRO]: false,
                    [PLANS.ADVANCED]: false,
                    [PLANS.ENTERPRISE]: true
                }
            ]
        },
        content: {
            title: t('subscription_invoices_page.content'),
            features: [
                {
                    feature: 'detailed_posts_analytics',
                    title: t('subscription_invoices_page.detailed_posts_analytics'),
                    tooltip: t('subscription_invoices_page.detailed_posts_analytics_tooltip'),
                    type: 'content',
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'detailed_stories_analytics',
                    title: t('subscription_invoices_page.detailed_stories_analytics'),
                    tooltip: t('subscription_invoices_page.detailed_stories_analytics_tooltip'),
                    type: 'content',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'albums',
                    title: t('subscription_invoices_page.albums'),
                    tooltip: t('subscription_invoices_page.albums_tooltip'),
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    type: 'content',
                    [PLANS.ADVANCED]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'labels',
                    title: t('subscription_invoices_page.labels'),
                    tooltip: t('subscription_invoices_page.labels_tooltip'),
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    type: 'content',
                    [PLANS.ADVANCED]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true
                }
            ]
        },
        publishing: {
            title: t('subscription_invoices_page.publishing'),
            features: [
                {
                    feature: 'post_scheduling',
                    title: t('subscription_invoices_page.post_scheduling'),
                    tooltip: t('subscription_invoices_page.post_scheduling_tooltip'),
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    type: 'publishing',
                    [PLANS.ADVANCED]: true,
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'stories_carousels_reels_scheduling',
                    title: t('subscription_invoices_page.stories_carousels_reels_scheduling'),
                    tooltip: t(
                        'subscription_invoices_page.stories_carousels_reels_scheduling_tooltip'
                    ),
                    networkTypes: [INSTAGRAM],
                    type: 'publishing',
                    [PLANS.PRO]: true,
                    [PLANS.ENTERPRISE]: true,
                    [PLANS.ADVANCED]: true
                },
                {
                    feature: 'crossposting',
                    title: t('subscription_invoices_page.crossposting'),
                    tooltip: t('subscription_invoices_page.crossposting_tooltip'),
                    type: 'publishing',
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'first_comment_scheduling',
                    title: t('subscription_invoices_page.first_comment_scheduling'),
                    tooltip: t('subscription_invoices_page.first_comment_scheduling_tooltip'),
                    type: 'publishing',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'media_library',
                    title: t('subscription_invoices_page.media_library'),
                    tooltip: t('subscription_invoices_page.media_library_tooltip'),
                    type: 'publishing',
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'saved_captions_hashtag_lists',
                    title: t('subscription_invoices_page.saved_captions_hashtag_lists'),
                    tooltip: t('subscription_invoices_page.saved_captions_hashtag_lists_tooltip'),
                    type: 'publishing',
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'post_approval_and_collaboration',
                    title: t('subscription_invoices_page.post_approval_and_collaboration'),
                    tooltip: t(
                        'subscription_invoices_page.post_approval_and_collaboration_tooltip'
                    ),
                    type: 'publishing',
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'ai_captions',
                    title: t('subscription_invoices_page.ai_captions'),
                    type: 'publishing',
                    [PLANS.PRO]: true,
                    networkTypes: [INSTAGRAM, FACEBOOK, TWITTER, LINKEDIN, TIKTOK_BUSINESS],
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                }
            ]
        },
        conversations: {
            title: t('subscription_invoices_page.conversations'),
            features: [
                {
                    feature: 'comments_tracking',
                    title: t('subscription_invoices_page.comments_tracking'),
                    tooltip: t('subscription_invoices_page.comments_tracking_tooltip'),
                    type: 'conversations',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'comments_export',
                    title: t('subscription_invoices_page.comments_export'),
                    tooltip: t('subscription_invoices_page.comments_export_tooltip'),
                    type: 'conversations',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'mentions_tracking',
                    title: t('subscription_invoices_page.mentions_tracking'),
                    tooltip: t('subscription_invoices_page.mentions_tracking_tooltip'),
                    type: 'conversations',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                }
            ]
        },
        listening: {
            title: t('subscription_invoices_page.listening'),
            features: [
                {
                    feature: 'competitors',
                    title: t('subscription_invoices_page.competitors'),
                    tooltip: t('subscription_invoices_page.competitors_tooltip'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: plans[PLANS.PRO].options.competitors?.count_included ?? null,
                    [PLANS.ADVANCED]:
                        plans[PLANS.ADVANCED].options.competitors?.count_included ?? null,
                    [PLANS.ENTERPRISE]:
                        plans[PLANS.ENTERPRISE].options.competitors?.count_included ?? null
                },
                {
                    feature: 'hashtags',
                    title: t('subscription_invoices_page.hashtags'),
                    tooltip: t('subscription_invoices_page.hashtags_tooltip'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: plans[PLANS.PRO].options.hashtags?.count_included ?? null,
                    [PLANS.ADVANCED]:
                        plans[PLANS.ADVANCED].options.hashtags?.count_included ?? null,
                    [PLANS.ENTERPRISE]:
                        plans[PLANS.ENTERPRISE].options.hashtags?.count_included ?? null
                },
                {
                    feature: 'customized_feeds',
                    title: t('subscription_invoices_page.customized_feeds'),
                    tooltip: t('subscription_invoices_page.customized_feeds_tooltip'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: plans[PLANS.PRO].options.feeds?.count_included ?? null,
                    [PLANS.ADVANCED]: plans[PLANS.ADVANCED].options.feeds?.count_included ?? null,
                    [PLANS.ENTERPRISE]:
                        plans[PLANS.ENTERPRISE].options.feeds?.count_included ?? null
                },
                {
                    feature: 'industry_benchmark',
                    title: t('subscription_invoices_page.industry_benchmark'),
                    tooltip: t('subscription_invoices_page.industry_benchmark_tooltip'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'additional_competitors',
                    title: t('subscription_invoices_page.additional_competitors'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: getFeaturePrice(plans[PLANS.PRO], 'competitors', t),
                    [PLANS.ADVANCED]: getFeaturePrice(plans[PLANS.ADVANCED], 'competitors', t),
                    [PLANS.ENTERPRISE]: getFeaturePrice(plans[PLANS.ENTERPRISE], 'competitors', t)
                },
                {
                    feature: 'additional_hashtags',
                    title: t('subscription_invoices_page.additional_hashtags'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: getFeaturePrice(plans[PLANS.PRO], 'hashtags', t),
                    [PLANS.ADVANCED]: getFeaturePrice(plans[PLANS.ADVANCED], 'hashtags', t),
                    [PLANS.ENTERPRISE]: getFeaturePrice(plans[PLANS.ENTERPRISE], 'hashtags', t)
                },
                {
                    feature: 'additional_customized_feeds',
                    title: t('subscription_invoices_page.additional_customized_feeds'),
                    type: 'listening',
                    networkTypes: [INSTAGRAM, FACEBOOK],
                    [PLANS.PRO]: getFeaturePrice(plans[PLANS.PRO], 'feeds', t),
                    [PLANS.ADVANCED]: getFeaturePrice(plans[PLANS.ADVANCED], 'feeds', t),
                    [PLANS.ENTERPRISE]: getFeaturePrice(plans[PLANS.ENTERPRISE], 'feeds', t)
                }
            ]
        },
        tools_services: {
            title: t('subscription_invoices_page.tools_services'),
            features: [
                /*{
                    feature: 'random_comment_picker_tool',
                    title: t('subscription_invoices_page.random_comment_picker_tool'),
                    type: 'tools_services',
                    networkTypes: [INSTAGRAM],
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },*/
                {
                    feature: 'mob_app',
                    title: t('subscription_invoices_page.mob_app'),
                    tooltip: t('subscription_invoices_page.mob_app_tooltip'),
                    type: 'tools_services',
                    networkTypes: null,
                    [PLANS.PRO]: true,
                    [PLANS.ADVANCED]: true,
                    [PLANS.ENTERPRISE]: true
                },
                {
                    feature: 'customer_success_program',
                    title: t('subscription_invoices_page.customer_success_program'),
                    tooltip: t('subscription_invoices_page.customer_success_program_tooltip'),
                    type: 'tools_services',
                    networkTypes: null,
                    [PLANS.PRO]: false,
                    [PLANS.ADVANCED]: false,
                    [PLANS.ENTERPRISE]: true
                }
            ]
        }
    };
};

export const PLANS_COLORS = {
    [PLANS.PRO]: {
        text: 'text-icoBlue',
        background: 'bg-icoBlue-100 dark:bg-icoBlue/20',
        button: 'primary',
        header: 'text-white bg-icoBlue dark:text-icoBlue dark:bg-icoBlue/20',
        border: 'border-icoBlue dark:border-icoBlue/20'
    },
    [PLANS.ADVANCED]: {
        text: 'text-icoGreen',
        background: 'bg-icoGreen-100 dark:bg-icoGreen/20',
        button: 'green',
        header: 'text-white bg-icoGreen dark:text-icoGreen dark:bg-icoGreen/20',
        border: 'border-icoGreen dark:border-icoGreen/20'
    },
    [PLANS.ENTERPRISE]: {
        text: 'text-icoPurple',
        background: 'bg-icoPurple-100 dark:bg-icoPurple/20',
        button: 'purple',
        header: 'text-white bg-icoPurple dark:text-icoPurple dark:bg-icoPurple/20',
        border: 'border-icoPurple dark:border-icoPurple/20'
    }
};
